import { useState, useEffect } from 'react'

interface SpotlightState {
  hasSeenTour: boolean
  tourEnabled: boolean
  lastVisit: string | null
}

const STORAGE_KEYS = {
  TOUR_COMPLETED: 'spotlight_tour_completed',
  TOUR_ENABLED: 'spotlight_tour_enabled',
  LAST_VISIT: 'spotlight_last_visit',
  TOUR_RESTART: 'spotlight_tour_restart',
}

const DAYS_UNTIL_RESHOWN = 7

export function useSpotlightTour() {
  const [shouldShowTour, setShouldShowTour] = useState(false)
  const [tourEnabled, setTourEnabled] = useState(true)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check if we should show the tour
    const checkTourStatus = () => {
      try {
        const hasSeenTour = localStorage.getItem(STORAGE_KEYS.TOUR_COMPLETED)
        const tourDisabled = localStorage.getItem(STORAGE_KEYS.TOUR_ENABLED)
        const lastVisitStr = localStorage.getItem(STORAGE_KEYS.LAST_VISIT)
        const shouldRestart = localStorage.getItem(STORAGE_KEYS.TOUR_RESTART)

        // Update tour enabled state
        setTourEnabled(tourDisabled !== 'false')

        // Manual restart requested
        if (shouldRestart === 'true') {
          localStorage.removeItem(STORAGE_KEYS.TOUR_RESTART)
          setShouldShowTour(true)
          setIsLoading(false)
          return
        }

        // Tour is disabled
        if (tourDisabled === 'false') {
          setShouldShowTour(false)
          setIsLoading(false)
          return
        }

        // New user (never seen tour)
        if (!hasSeenTour) {
          setShouldShowTour(true)
          setIsLoading(false)
          return
        }

        // Check if user is returning after 7+ days
        if (lastVisitStr) {
          const lastVisit = new Date(lastVisitStr)
          const daysSinceLastVisit = Math.floor(
            (Date.now() - lastVisit.getTime()) / (1000 * 60 * 60 * 24),
          )

          if (daysSinceLastVisit >= DAYS_UNTIL_RESHOWN) {
            // Reset tour completion for returning users
            localStorage.removeItem(STORAGE_KEYS.TOUR_COMPLETED)
            setShouldShowTour(true)
            setIsLoading(false)
            return
          }
        }

        // Default: don't show tour
        setShouldShowTour(false)
        setIsLoading(false)
      } catch (error) {
        console.error('Error checking tour status:', error)
        setIsLoading(false)
      }
    }

    // Small delay to let the page settle
    const timer = setTimeout(checkTourStatus, 500)

    return () => clearTimeout(timer)
  }, [])

  // Update last visit timestamp
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.LAST_VISIT, new Date().toISOString())
    } catch (error) {
      console.error('Error updating last visit:', error)
    }
  }, [])

  const completeTour = () => {
    try {
      localStorage.setItem(STORAGE_KEYS.TOUR_COMPLETED, 'true')
      setShouldShowTour(false)
    } catch (error) {
      console.error('Error completing tour:', error)
    }
  }

  const dismissTour = () => {
    try {
      localStorage.setItem(STORAGE_KEYS.TOUR_COMPLETED, 'true')
      setShouldShowTour(false)
    } catch (error) {
      console.error('Error dismissing tour:', error)
    }
  }

  const restartTour = () => {
    try {
      localStorage.setItem(STORAGE_KEYS.TOUR_RESTART, 'true')
      localStorage.removeItem(STORAGE_KEYS.TOUR_COMPLETED)
      setShouldShowTour(true)
    } catch (error) {
      console.error('Error restarting tour:', error)
    }
  }

  const toggleTourEnabled = (enabled: boolean) => {
    try {
      localStorage.setItem(STORAGE_KEYS.TOUR_ENABLED, enabled.toString())
      setTourEnabled(enabled)
      if (!enabled) {
        setShouldShowTour(false)
      }
    } catch (error) {
      console.error('Error toggling tour:', error)
    }
  }

  return {
    shouldShowTour,
    tourEnabled,
    isLoading,
    completeTour,
    dismissTour,
    restartTour,
    toggleTourEnabled,
  }
}

export default useSpotlightTour
